/**
 * Form Helpers - Shared conversion functions for Item/SubItem forms
 *
 * Centralized here to avoid duplication across ItemEditor, SubItemEditorPage, etc.
 */

import { getDefaultFormValues, type ItemFormData } from '../components/fields';
import type {
  Item,
  SubItem,
  ItemType,
  CreateItemInput,
  CreateSubItemInput,
  UpdateItemInput,
  UpdateSubItemInput,
  Reminder,
  NoteColor,
} from '@perfin/shared';
import { DEFAULT_EFFORT_HOURS, normalizeEffortHours } from '@perfin/shared';

// ============================================================================
// ITEM TO FORM DATA CONVERSIONS
// ============================================================================

/**
 * Convert an Item to form data
 */
export function itemToFormData(item: Item): ItemFormData {
  const base = getDefaultFormValues(item.type);
  const anyItem = item as unknown as Record<string, unknown>;

  // Reminders are always in new format (no legacy number[] support needed)
  const reminders: Reminder[] = Array.isArray(anyItem.reminders)
    ? (anyItem.reminders as Reminder[])
    : [];

  const dueDate = (anyItem.dueDate as { toDate: () => Date } | undefined)?.toDate() || null;
  const recurrence = (anyItem.recurrence as string) || 'none';
  // Infer scheduleLater: true when dueDate is null and item is non-recurring
  const scheduleLater = !dueDate && recurrence === 'none';

  return {
    ...base,
    name: item.name || '',
    description: item.description || '',
    category: item.category || '',
    subCategory: item.subCategory || '',
    itemGroup: (anyItem.itemGroup as string) || 'None',
    owner: item.owner || '',
    priority: item.priority || 'medium',
    dueDate,
    startDate: (anyItem.startDate as { toDate: () => Date } | undefined)?.toDate() || null,
    endDate: (anyItem.endDate as { toDate: () => Date } | undefined)?.toDate() || null,
    endDateForever: (anyItem.endDateForever as boolean) || false,
    recurrence,
    daysOfWeek: (anyItem.daysOfWeek as number[]) || [],
    daysOfMonth: (anyItem.daysOfMonth as (number | string)[]) || [],
    datesOfYear: (anyItem.datesOfYear as string[]) || [],
    scheduleLater,
    status: item.type === 'task' ? (item.status || 'new') : ((item as unknown as { status?: string }).status || 'draft'),
    choreWheel: item.type === 'task' ? ((anyItem.choreWheel as boolean) ?? false) : false,
    effortHours: normalizeEffortHours(anyItem.effortHours as number | undefined),
    transactionType: item.type === 'transaction' ? (item.transactionType || 'expense') : 'expense',
    amount: item.type === 'transaction' ? ((item.amount || 0) / 100).toFixed(2) : '',
    currency: item.type === 'transaction' ? (item.currency || 'USD') : 'USD',
    entityId: item.type === 'transaction' ? (item.entityId || '') : '',
    accountId: item.type === 'transaction' ? (item.accountId || '') : '',
    content: item.type === 'note' ? (item.content || '') : '',
    color: item.type === 'note' ? (item.color || 'default') : 'default',
    tags: item.type === 'note' ? (item.tags || []) : [],
    urls: (anyItem.urls as string[]) || [],
    reminders,
  };
}

/**
 * Convert a SubItem to form data
 * Note: Sub-items don't have itemGroup, recurrence, choreWheel - use defaults for those
 */
export function subItemToFormData(subItem: SubItem): ItemFormData {
  const base = getDefaultFormValues(subItem.type);
  const anyItem = subItem as unknown as Record<string, unknown>;

  // Reminders are always in new format
  const reminders: Reminder[] = Array.isArray(anyItem.reminders)
    ? (anyItem.reminders as Reminder[])
    : [];

  const dueDate = (anyItem.dueDate as { toDate: () => Date } | undefined)?.toDate() || null;

  return {
    ...base,
    name: subItem.name || '',
    description: subItem.description || '',
    category: subItem.category || '',
    subCategory: subItem.subCategory || '',
    // Sub-items don't have itemGroup
    itemGroup: 'None',
    owner: subItem.owner || '',
    priority: subItem.priority || 'medium',
    // Sub-items only have dueDate, no recurrence fields
    dueDate,
    startDate: null,
    endDate: null,
    endDateForever: false,
    recurrence: 'none',
    daysOfWeek: [],
    daysOfMonth: [],
    datesOfYear: [],
    // Infer scheduleLater for sub-items (always non-recurring)
    scheduleLater: !dueDate,
    status: subItem.type === 'task' ? (subItem.status || 'new') : ((subItem as unknown as { status?: string }).status || 'draft'),
    // Sub-items don't have choreWheel
    choreWheel: false,
    effortHours: normalizeEffortHours(anyItem.effortHours as number | undefined),
    transactionType: subItem.type === 'transaction' ? (subItem.transactionType || 'expense') : 'expense',
    amount: subItem.type === 'transaction' ? ((subItem.amount || 0) / 100).toFixed(2) : '',
    currency: subItem.type === 'transaction' ? (subItem.currency || 'USD') : 'USD',
    entityId: subItem.type === 'transaction' ? (subItem.entityId || '') : '',
    accountId: subItem.type === 'transaction' ? (subItem.accountId || '') : '',
    content: subItem.type === 'note' ? (subItem.content || '') : '',
    color: subItem.type === 'note' ? (subItem.color || 'default') : 'default',
    tags: subItem.type === 'note' ? (subItem.tags || []) : [],
    urls: (anyItem.urls as string[]) || [],
    reminders,
  };
}

/**
 * Convert a CreateSubItemInput (pending sub-item) to form data
 * Note: Sub-items don't have itemGroup, recurrence, choreWheel - use defaults for those
 */
export function pendingInputToFormData(input: CreateSubItemInput, currentUserId?: string): ItemFormData {
  const base = getDefaultFormValues(input.type);
  const anyInput = input as unknown as Record<string, unknown>;

  const dueDate = (anyInput.dueDate as Date) || null;

  return {
    ...base,
    name: input.name || '',
    description: input.description || '',
    category: (anyInput.category as string) || '',
    subCategory: (anyInput.subCategory as string) || '',
    // Sub-items don't have itemGroup
    itemGroup: 'None',
    owner: (anyInput.owner as string) || currentUserId || '',
    priority: input.priority || 'medium',
    // Sub-items only have dueDate, no recurrence fields
    dueDate,
    startDate: null,
    endDate: null,
    endDateForever: false,
    recurrence: 'none',
    daysOfWeek: [],
    daysOfMonth: [],
    datesOfYear: [],
    scheduleLater: !dueDate,
    status: input.type === 'task' ? ((anyInput.status as string) || 'new') : ((anyInput.status as string) || 'draft'),
    // Sub-items don't have choreWheel
    choreWheel: false,
    effortHours: normalizeEffortHours(anyInput.effortHours as number | undefined),
    transactionType: input.type === 'transaction' ? ((anyInput.transactionType as string) || 'expense') : 'expense',
    amount: input.type === 'transaction' ? ((anyInput.amount as number || 0) / 100).toFixed(2) : '',
    currency: input.type === 'transaction' ? ((anyInput.currency as string) || 'USD') : 'USD',
    entityId: input.type === 'transaction' ? ((anyInput.entityId as string) || '') : '',
    accountId: input.type === 'transaction' ? ((anyInput.accountId as string) || '') : '',
    content: input.type === 'note' ? ((anyInput.content as string) || '') : '',
    color: input.type === 'note' ? ((anyInput.color as string) || 'default') : 'default',
    tags: input.type === 'note' ? ((anyInput.tags as string[]) || []) : [],
    urls: (anyInput.urls as string[]) || [],
    reminders: (anyInput.reminders as Reminder[]) || [],
  };
}

// ============================================================================
// FORM DATA TO RECORD CONVERSIONS
// ============================================================================

/**
 * Convert form data to a generic record (for draft storage)
 */
export function formDataToRecord(data: ItemFormData): Record<string, unknown> {
  return data as unknown as Record<string, unknown>;
}

/**
 * Convert a generic record back to form data
 * Handles restoration of Date objects from JSON strings
 */
export function recordToFormData(record: Record<string, unknown>, type: ItemType): ItemFormData {
  const defaults = getDefaultFormValues(type);
  const result = {
    ...defaults,
    ...record,
  } as ItemFormData;

  // Restore Date objects from JSON strings
  const restoreDate = (value: unknown): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'string') return new Date(value);
    if (typeof value === 'object' && 'seconds' in value) {
      return new Date((value as { seconds: number }).seconds * 1000);
    }
    return null;
  };

  result.dueDate = restoreDate(result.dueDate);
  result.startDate = restoreDate(result.startDate);
  result.endDate = restoreDate(result.endDate);

  // Ensure boolean fields are properly typed
  if (typeof result.endDateForever !== 'boolean') {
    result.endDateForever = result.endDateForever === true || result.endDateForever === 'true';
  }

  // Ensure effortHours is a valid number
  if (typeof result.effortHours !== 'number' || isNaN(result.effortHours)) {
    result.effortHours = DEFAULT_EFFORT_HOURS;
  }

  // Ensure arrays are properly initialized
  if (!Array.isArray(result.urls)) result.urls = [];
  if (!Array.isArray(result.tags)) result.tags = [];
  if (!Array.isArray(result.reminders)) result.reminders = [];
  if (!Array.isArray(result.daysOfWeek)) result.daysOfWeek = [];
  if (!Array.isArray(result.daysOfMonth)) result.daysOfMonth = [];
  if (!Array.isArray(result.datesOfYear)) result.datesOfYear = [];

  return result;
}

// ============================================================================
// FORM DATA TO INPUT CONVERSIONS
// ============================================================================

/**
 * Convert form data to CreateItemInput
 * @param organizationId - User's organizationId (for org context)
 */
export function formDataToCreateItemInput(data: ItemFormData, type: ItemType, organizationId?: string): CreateItemInput {
  const base = {
    name: data.name,
    description: data.description || undefined,
    category: data.category || 'None',
    subCategory: data.subCategory || 'None',
    itemGroup: data.itemGroup || 'None',
    organizationId, // Always required for org-scoped items
    owner: data.owner || undefined,
    priority: data.priority as 'critical' | 'high' | 'medium' | 'low' | 'future',
    effortHours: normalizeEffortHours(data.effortHours),
    urls: data.urls.length > 0 ? data.urls : undefined,
    reminders: data.reminders.length > 0 ? data.reminders : undefined,
  };

  // Helper to compute date fields based on recurrence
  // recurrence = 'none': use dueDate, clear startDate/endDate
  // recurrence != 'none': use startDate/endDate, clear dueDate
  const getDateFields = (hasRecurrence: boolean) => {
    if (!hasRecurrence || data.recurrence === 'none') {
      return {
        dueDate: data.dueDate || undefined,
        startDate: undefined,
        endDate: undefined,
        endDateForever: undefined,
      };
    }
    // Recurring: use startDate/endDate
    // If endDateForever is true, set endDate to 100 years from start
    let endDate = data.endDate || undefined;
    if (data.endDateForever && data.startDate) {
      const farFuture = new Date(data.startDate);
      farFuture.setFullYear(farFuture.getFullYear() + 100);
      endDate = farFuture;
    }
    return {
      dueDate: undefined,
      startDate: data.startDate || undefined,
      endDate,
      endDateForever: data.endDateForever || undefined,
    };
  };

  switch (type) {
    case 'task': {
      const dateFields = getDateFields(true);
      return {
        ...base,
        type: 'task' as const,
        status: data.status as 'new' | 'inProgress' | 'complete',
        choreWheel: data.choreWheel ?? undefined,
        ...dateFields,
        recurrence: data.recurrence !== 'none' ? data.recurrence as 'daily' | 'weekly' | 'monthly' | 'yearly' : undefined,
        daysOfWeek: data.daysOfWeek.length > 0 ? data.daysOfWeek : undefined,
        daysOfMonth: data.daysOfMonth.length > 0 ? data.daysOfMonth : undefined,
        datesOfYear: data.datesOfYear.length > 0 ? data.datesOfYear : undefined,
      };
    }

    case 'transaction': {
      const dateFields = getDateFields(true);
      return {
        ...base,
        type: 'transaction' as const,
        transactionType: data.transactionType as 'expense' | 'income',
        amount: Math.round(parseFloat(data.amount || '0') * 100),
        currency: data.currency,
        entityId: data.entityId || 'None',
        accountId: data.accountId || 'None',
        status: data.status as 'draft' | 'pending' | 'final',
        ...dateFields,
        recurrence: data.recurrence !== 'none' ? data.recurrence as 'daily' | 'weekly' | 'monthly' | 'yearly' : undefined,
        daysOfWeek: data.daysOfWeek.length > 0 ? data.daysOfWeek : undefined,
        daysOfMonth: data.daysOfMonth.length > 0 ? data.daysOfMonth : undefined,
        datesOfYear: data.datesOfYear.length > 0 ? data.datesOfYear : undefined,
      };
    }

    case 'note':
      // Notes: only dueDate for reminders, no recurrence
      return {
        ...base,
        type: 'note' as const,
        content: data.content || undefined,
        color: data.color as NoteColor,
        tags: data.tags.length > 0 ? data.tags : undefined,
        status: data.status as 'draft' | 'final',
        dueDate: data.dueDate || undefined,
      };

    case 'keyDate': {
      const dateFields = getDateFields(true);
      return {
        ...base,
        type: 'keyDate' as const,
        ...dateFields,
        recurrence: data.recurrence !== 'none' ? data.recurrence as 'daily' | 'weekly' | 'monthly' | 'yearly' : undefined,
        daysOfWeek: data.daysOfWeek.length > 0 ? data.daysOfWeek : undefined,
        daysOfMonth: data.daysOfMonth.length > 0 ? data.daysOfMonth : undefined,
        datesOfYear: data.datesOfYear.length > 0 ? data.datesOfYear : undefined,
        status: data.status as 'draft' | 'final',
      };
    }
  }
}

/**
 * Convert form data to UpdateItemInput
 */
export function formDataToUpdateItemInput(data: ItemFormData, type: ItemType, organizationId?: string): UpdateItemInput {
  return formDataToCreateItemInput(data, type, organizationId) as UpdateItemInput;
}

/**
 * Convert form data to CreateSubItemInput
 * Sub-items don't have: itemGroup, recurrence, choreWheel, startDate, endDate, endDateForever, daysOfWeek, daysOfMonth, datesOfYear
 */
export function formDataToCreateSubItemInput(data: ItemFormData, type: ItemType, organizationId?: string): CreateSubItemInput {
  const base = {
    name: data.name,
    description: data.description || undefined,
    category: data.category || 'None',
    subCategory: data.subCategory || 'None',
    // No itemGroup for sub-items
    organizationId,
    owner: data.owner || undefined,
    priority: data.priority as 'critical' | 'high' | 'medium' | 'low' | 'future',
    effortHours: normalizeEffortHours(data.effortHours),
    urls: data.urls.length > 0 ? data.urls : undefined,
    reminders: data.reminders.length > 0 ? data.reminders : undefined,
    // Sub-items only have dueDate, no recurrence fields
    dueDate: data.dueDate || undefined,
  };

  switch (type) {
    case 'task':
      return {
        ...base,
        type: 'task' as const,
        status: data.status as 'new' | 'inProgress' | 'complete',
        // No choreWheel for sub-items
      };

    case 'transaction':
      return {
        ...base,
        type: 'transaction' as const,
        transactionType: data.transactionType as 'expense' | 'income',
        amount: Math.round(parseFloat(data.amount || '0') * 100),
        currency: data.currency,
        entityId: data.entityId || 'None',
        accountId: data.accountId || 'None',
        status: data.status as 'draft' | 'pending' | 'final',
      };

    case 'note':
      return {
        ...base,
        type: 'note' as const,
        content: data.content || undefined,
        color: data.color as NoteColor,
        tags: data.tags.length > 0 ? data.tags : undefined,
        status: data.status as 'draft' | 'final',
      };

    case 'keyDate':
      return {
        ...base,
        type: 'keyDate' as const,
        status: data.status as 'draft' | 'final',
      };
  }
}

/**
 * Convert form data to UpdateSubItemInput
 */
export function formDataToUpdateSubItemInput(data: ItemFormData, type: ItemType, organizationId?: string): UpdateSubItemInput {
  return formDataToCreateSubItemInput(data, type, organizationId) as UpdateSubItemInput;
}

// ============================================================================
// TIMESTAMP FORMATTING
// ============================================================================

/**
 * Format a Firestore Timestamp or Date for display in editor header
 * Format: mm-dd-yyyy h:mm am/pm (no leading zero on hour, leading zero on minutes)
 */
export function formatEditorTimestamp(timestamp: { toDate: () => Date } | Date | null | undefined): string | null {
  if (!timestamp) return null;

  const date = timestamp instanceof Date ? timestamp : timestamp.toDate();

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12;
  if (hours === 0) hours = 12; // 12 am/pm instead of 0

  return `${month}-${day}-${year} ${hours}:${minutes} ${ampm}`;
}
